// CL-4 aiFetch wrapper (omo A-12 영감).
//
// OpenAI 호출을 단일 wrapper로 통과 — transient error (429/502/503/504/529 + classifier rate_limit)
// transparent retry + AbortController timeout. cooldown 8s/16s/32s exponential cap 60s.
//
// non-transient error (context_limit / tool_result_missing / invalid_*)는 즉시 throw —
// RECOVERY_TABLE handler 영역으로 위임 (CL-1 dispatchRecovery).
//
// 의존성: CL-1 classifyOpenAIError + ERROR_TYPES
//
// 사용 시점:
//   - caller가 OpenAI 호출 진입점을 aiFetch(() => openai.chat.completions.create(params))로 wrap
//   - signal을 받는 형태도 호환 — aiFetch((signal) => openai.chat.completions.create({...}, {signal}))

import { classifyOpenAIError, ERROR_TYPES } from './errorClassifier.js';

const DEFAULT_MAX_ATTEMPTS = 3;
// 기본 30초. AI_FETCH_TIMEOUT_MS env로 상향 가능(로컬 LLM 스모크: 콜드로드+thinking 지연 대응). 미설정 시 기존과 동일 → 라이브 무영향.
const DEFAULT_TIMEOUT_MS = Number(process.env.AI_FETCH_TIMEOUT_MS) || 30_000;
const COOLDOWN_BASE_MS = 8_000;
const COOLDOWN_CAP_MS = 60_000;

const TRANSIENT_HTTP_STATUSES = Object.freeze(new Set([429, 502, 503, 504, 529]));

/**
 * @param {Function} requestFn  — async (signal?) => result
 * @param {object} [options]
 * @returns {Promise<any>}
 */
export async function aiFetch(requestFn, options = {}) {
  if (typeof requestFn !== 'function') {
    throw new TypeError('aiFetch: requestFn must be a function');
  }
  const maxAttempts = options.maxAttempts ?? DEFAULT_MAX_ATTEMPTS;
  const timeoutMs = options.timeoutMs ?? DEFAULT_TIMEOUT_MS;
  const sleep = typeof options.sleep === 'function' ? options.sleep : _defaultSleep;
  const onAttempt = typeof options.onAttempt === 'function' ? options.onAttempt : null;

  let lastErr = null;
  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    if (onAttempt) {
      try { onAttempt(attempt, lastErr); } catch { /* swallow hook errors */ }
    }
    try {
      return await _withTimeout(requestFn, timeoutMs);
    } catch (err) {
      lastErr = err;
      const isTransient = _isTransient(err);
      if (!isTransient || attempt === maxAttempts) {
        throw err;
      }
      const cooldown = Math.min(COOLDOWN_BASE_MS * Math.pow(2, attempt - 1), COOLDOWN_CAP_MS);
      await sleep(cooldown);
    }
  }
  // unreachable — loop always returns or throws
  throw lastErr;
}

// ────────────────────────────────────────────────
// transient 판정
// ────────────────────────────────────────────────

function _isTransient(err) {
  if (!err) return false;
  const status = err.status ?? err.statusCode ?? err.response?.status ?? null;
  if (TRANSIENT_HTTP_STATUSES.has(status)) return true;
  const type = classifyOpenAIError(err);
  if (type === ERROR_TYPES.RATE_LIMIT) return true;
  // context_limit / tool_result_missing / empty_message / invalid_* 는 transient X
  // — 재시도해도 동일 error. RECOVERY_TABLE handler 영역으로 위임.
  return false;
}

// ────────────────────────────────────────────────
// timeout wrapper — AbortController race
// ────────────────────────────────────────────────

function _withTimeout(requestFn, timeoutMs) {
  if (!timeoutMs || timeoutMs <= 0) {
    // timeout 비활성 — 직접 호출
    return Promise.resolve().then(() => requestFn());
  }
  const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
  return new Promise((resolve, reject) => {
    let settled = false;
    const timer = setTimeout(() => {
      if (settled) return;
      settled = true;
      if (controller) {
        try { controller.abort(); } catch { /* abort can throw in some envs */ }
      }
      const err = new Error(`aiFetch timeout after ${timeoutMs}ms`);
      err.code = 'aiFetch_timeout';
      reject(err);
    }, timeoutMs);

    Promise.resolve()
      .then(() => requestFn(controller?.signal))
      .then((result) => {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        resolve(result);
      })
      .catch((e) => {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        reject(e);
      });
  });
}

function _defaultSleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export const _isTransientForTest = _isTransient;
export const _withTimeoutForTest = _withTimeout;
export const _COOLDOWN_BASE_MS = COOLDOWN_BASE_MS;
export const _COOLDOWN_CAP_MS = COOLDOWN_CAP_MS;
export const _DEFAULT_MAX_ATTEMPTS = DEFAULT_MAX_ATTEMPTS;
export const _DEFAULT_TIMEOUT_MS = DEFAULT_TIMEOUT_MS;
export const _TRANSIENT_HTTP_STATUSES = TRANSIENT_HTTP_STATUSES;
